import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import { PageHeader, PageShell } from "../../../components/UI/Page";
import { EmptyState, LoadingState } from "../../../components/UI/States";
import {
  ActivityCalendar,
  BackLink,
  InspectionDialog,
  InspectionTable,
  SearchField,
  Segmented,
} from "../../../components/Fleet/FleetParts";
import { HISTORY_DAYS, isFlagged, localDayKey } from "../../../components/Fleet/fleetFormat";
import { fetchKioskHistory } from "../fleetApi";

// Check-in history for one unit, opened from the Last Check-In date on the
// branch kiosk (ForkDisplay). Public like the kiosk itself: no app header,
// no sign-in. Shows the last 90 days from both the mobile app and the kiosk;
// ?branch= narrows the match when the same unit number exists at more than
// one branch.
const DisplayEntries = () => {
  const { unit } = useParams();
  const [params] = useSearchParams();
  const branch = params.get("branch") || "";
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState("all");
  const [day, setDay] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let active = true;
    setEntries(null);
    setError(null);
    fetchKioskHistory({ unit, branch })
      .then((data) => active && setEntries(data.entries))
      .catch((e) => active && setError(e.message));
    return () => {
      active = false;
    };
  }, [unit, branch]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (entries || [])
      .filter((e) => filter === "all" || isFlagged(e))
      .filter((e) => !day || localDayKey(e.at) === day)
      .filter(
        (e) =>
          !q ||
          [e.inspector, e.notes, ...e.flagged.flatMap((f) => [f.label, f.note])]
            .filter(Boolean)
            .some((text) => text.toLowerCase().includes(q))
      );
  }, [entries, filter, day, query]);

  const flaggedCount = (entries || []).filter(isFlagged).length;

  return (
    // Standalone page: rendered with authHeader/footer off (see RouteHandler),
    // so it needs its own full-page themed surface.
    <div className="app-page min-h-screen">
      <PageShell size="lg">
        <InspectionDialog entry={open} onClose={() => setOpen(null)} />

        {branch && (
          <BackLink to={`/fleet/forks/dashboard/${encodeURIComponent(branch)}`}>
            {branch} forklifts
          </BackLink>
        )}

        <PageHeader
          icon={HistoryRoundedIcon}
          tone="teal"
          eyebrow="Forklift check-ins"
          title={`Unit #${unit}`}
          description={`Every check-in from the last ${HISTORY_DAYS} days — from the branch kiosk and the mobile app.`}
          actions={
            entries?.length > 0 && (
              <SearchField value={query} onChange={setQuery} placeholder="Search name or item…" className="w-full sm:w-64" />
            )
          }
        />

        {error && (
          <div className="app-card rounded-2xl border">
            <EmptyState compact icon={HistoryRoundedIcon} title="Couldn't load check-ins." description={error} />
          </div>
        )}

        {!entries && !error && <LoadingState label="Loading check-ins…" />}

        {entries && entries.length === 0 && (
          <div className="app-card rounded-2xl border">
            <EmptyState
              compact
              icon={HistoryRoundedIcon}
              title={`No check-ins for unit #${unit} in the last ${HISTORY_DAYS} days.`}
            />
          </div>
        )}

        {entries && entries.length > 0 && (
          <div className="space-y-4">
            <div className="app-card rounded-2xl border p-4 sm:p-5">
              <ActivityCalendar entries={entries} selectedDay={day} onSelectDay={setDay} noun="check-in" />
            </div>
            <div className="app-card overflow-hidden rounded-2xl border">
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                <p className="text-sm font-semibold tabular-nums">
                  {shown.length} check-in{shown.length === 1 ? "" : "s"}
                  {day && (
                    <button type="button" className="app-link ml-2 text-xs font-normal" onClick={() => setDay(null)}>
                      Show all days
                    </button>
                  )}
                </p>
                <Segmented
                  label="Filter check-ins"
                  value={filter}
                  onChange={setFilter}
                  options={[
                    { value: "all", label: "All", count: entries.length },
                    { value: "flagged", label: "Flagged", count: flaggedCount },
                  ]}
                />
              </div>
              <div className="border-t app-divider">
                <InspectionTable
                  entries={shown}
                  onOpen={setOpen}
                  show={{ source: true, branch: !branch }}
                  emptyTitle="No check-ins match."
                />
              </div>
            </div>
          </div>
        )}
      </PageShell>
    </div>
  );
};

export default DisplayEntries;
